import { ClearOutlined, SearchOutlined } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  FormHelperText,
  IconButton,
  InputAdornment,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { TemplateListSerialised } from "@providers/hop-ord-server/api";
import { CustomTheme } from "@mui/material/styles";
import { useMemo, useState } from "react";

interface Props {
  templates: TemplateListSerialised[];
  selectedTemplate: string;
  onSelect: (templateName: string) => void;
  error?: boolean;
}

const TABLE_MAX_HEIGHT = 250;
const DOSE_LEVEL_COL_WIDTH = 120;

const TemplateSelectionTable = ({
  templates,
  selectedTemplate,
  onSelect,
  error = false,
}: Props): JSX.Element => {
  const [searchQuery, setSearchQuery] = useState("");
  const theme = useTheme<CustomTheme>();

  const sortedTemplates = useMemo(
    () => [...templates].sort((a, b) => a.name.localeCompare(b.name)),
    [templates],
  );

  const filteredTemplates = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return sortedTemplates;

    const nameMatches: TemplateListSerialised[] = [];
    const descriptionOnlyMatches: TemplateListSerialised[] = [];

    for (const t of sortedTemplates) {
      if (t.name.toLowerCase().includes(query)) {
        nameMatches.push(t);
      } else if (t.description?.toLowerCase().includes(query)) {
        descriptionOnlyMatches.push(t);
      }
    }

    return [...nameMatches, ...descriptionOnlyMatches];
  }, [searchQuery, sortedTemplates]);

  return (
    <Box>
      <Card
        variant="outlined"
        sx={{
          borderColor: error ? theme.palette.error.main : undefined,
        }}
      >
        <CardContent sx={{ pb: "16px !important" }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Automated templates
          </Typography>

          <TextField
            size="small"
            placeholder="Template name"
            label="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            slotProps={{
              htmlInput: { "aria-label": "Search templates" },
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    {searchQuery ? (
                      <IconButton
                        size="small"
                        aria-label="Clear search"
                        onClick={() => setSearchQuery("")}
                        edge="end"
                      >
                        <ClearOutlined fontSize="small" />
                      </IconButton>
                    ) : (
                      <SearchOutlined fontSize="small" color="action" />
                    )}
                  </InputAdornment>
                ),
              },
            }}
            sx={{ mb: 0.75, width: 245 }}
          />

          <Box sx={{ overflow: "auto", scrollbarGutter: "stable" }}>
            <Table size="small">
              <colgroup>
                <col />
                <col style={{ width: DOSE_LEVEL_COL_WIDTH }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ p: 2 }}>Name and description</TableCell>
                  <TableCell
                    sx={{
                      pl: 2,
                      pr: 1,
                      py: 2,
                      letterSpacing: "0.17px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Dose level
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </Box>

          <TableContainer
            sx={{
              maxHeight: TABLE_MAX_HEIGHT,
              overflow: "auto",
              scrollbarGutter: "stable",
            }}
          >
            <Table size="small">
              <colgroup>
                <col />
                <col style={{ width: DOSE_LEVEL_COL_WIDTH }} />
              </colgroup>
              <TableBody>
                {filteredTemplates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ py: 4 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        No results found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTemplates.map((template, index) => {
                    const isSelected = selectedTemplate === template.name;
                    const isLastRow = index === filteredTemplates.length - 1;
                    const cellStyle = isLastRow ? { borderBottom: "none" } : {};

                    return (
                      <TableRow
                        data-testid={`template-selection-table-row-${index}`}
                        key={template.name}
                        hover
                        selected={isSelected}
                        onClick={() => onSelect(template.name)}
                        sx={{
                          cursor: "pointer",
                          transition: theme.transitions.create(
                            ["background-color"],
                            {
                              duration: theme.transitions.duration.standard,
                              easing: theme.transitions.easing.easeInOut,
                            },
                          ),
                        }}
                      >
                        <TableCell sx={{ ...cellStyle, pl: 0.5, py: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Radio
                              checked={isSelected}
                              size="small"
                              tabIndex={-1}
                            />
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                }}
                              >
                                {template.name}
                              </Typography>
                              {template.description && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                  }}
                                >
                                  {template.description}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...cellStyle, pl: 2, pr: 1, py: 1 }}>
                          <Typography variant="body2">
                            {template.doseLevelDisplay ?? "-"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {error && (
        <FormHelperText error sx={{ mx: "14px" }}>
          This field is required
        </FormHelperText>
      )}
    </Box>
  );
};

export default TemplateSelectionTable;
